import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearch, useNavigate } from '@tanstack/react-router'
import { Layout } from '@/components/layout'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { contactsService, type Contactgegeven } from '@/lib/services/contactsService'
import { Card } from '@/components/ui/card'
import {
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  Building2,
  User,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'

export function ContactsListPage() {
  const { t } = useTranslation()
  const { activeCrisis } = useCrisisStore()
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '/contacts' })
  const [activeTab, setActiveTab] = useState<string>('')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState<string>('')

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab)
    // Update URL without tab parameter (removing it when manually switching)
    navigate({ to: '/contacts', search: { tab: undefined }, replace: true })
  }

  const toggleRow = (rowId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(rowId)) {
        newSet.delete(rowId)
      } else {
        newSet.add(rowId)
      }
      return newSet
    })
  }

  useEffect(() => {
    if (activeCrisis) {
      document.title = `${t('contacts.titel')} - ${activeCrisis.type} - BGCH Crisis Buddy`
    } else {
      document.title = `${t('contacts.titel')} - BGCH Crisis Buddy`
    }
  }, [activeCrisis, t])

  const {
    data: contactgegevens,
    isLoading,
    error,
  } = useQuery<Contactgegeven[]>({
    queryKey: ['contactgegevens', activeCrisis?.id],
    queryFn: () => contactsService.getContactgegevens(),
    enabled: !!activeCrisis,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Filter contacts based on search query
  const filteredContactgegevens = useMemo(() => {
    if (!contactgegevens || !searchQuery.trim()) return contactgegevens

    const query = searchQuery.toLowerCase().trim()
    return contactgegevens.filter(contact => {
      return (
        contact.organisatie?.toLowerCase().includes(query) ||
        contact.naamFunctie?.toLowerCase().includes(query) ||
        contact.telefoonVast?.toLowerCase().includes(query) ||
        contact.mobiel?.toLowerCase().includes(query) ||
        contact.mail?.toLowerCase().includes(query) ||
        contact.calamiteitennr?.toLowerCase().includes(query) ||
        contact.categorie?.toLowerCase().includes(query) ||
        contact.subcategorie?.toLowerCase().includes(query)
      )
    })
  }, [contactgegevens, searchQuery])

  // Group contacts by bestand (file)
  const groupedByBestand = useMemo(() => {
    if (!filteredContactgegevens) return {}

    return filteredContactgegevens.reduce(
      (acc, contact) => {
        const bestand = contact.bestand || 'Onbekend'
        if (!acc[bestand]) {
          acc[bestand] = []
        }
        acc[bestand].push(contact)
        return acc
      },
      {} as Record<string, Contactgegeven[]>
    )
  }, [filteredContactgegevens])

  // Group contacts within each bestand by categorie
  const groupedByBestandAndCategorie = useMemo(() => {
    const result: Record<string, Record<string, Contactgegeven[]>> = {}

    Object.entries(groupedByBestand).forEach(([bestand, contacts]) => {
      result[bestand] = contacts.reduce(
        (acc, contact) => {
          const categorie = contact.categorie || 'Overig'
          if (!acc[categorie]) {
            acc[categorie] = []
          }
          acc[categorie].push(contact)
          return acc
        },
        {} as Record<string, Contactgegeven[]>
      )
    })

    return result
  }, [groupedByBestand])

  const bestandTypes = useMemo(() => Object.keys(groupedByBestand), [groupedByBestand])

  // Set active tab from URL parameter or default to first bestand type when data loads
  useEffect(() => {
    if (bestandTypes.length > 0) {
      if (searchParams?.tab) {
        // If tab parameter is provided, try to find matching bestand
        const matchingTab = bestandTypes.find(bestand =>
          bestand.includes(searchParams.tab as string)
        )
        if (matchingTab) {
          setActiveTab(matchingTab)
        } else {
          // If no match found, default to first tab
          setActiveTab(bestandTypes[0])
        }
      } else if (!activeTab) {
        // No tab parameter, default to first tab
        setActiveTab(bestandTypes[0])
      }
    }
  }, [bestandTypes, searchParams?.tab, activeTab])

  // Render contacts grouped by categorie with expandable subcategories
  const renderContactsByCategorie = (contactsByCategorie: Record<string, Contactgegeven[]>) => {
    const categories = Object.keys(contactsByCategorie).sort()

    return (
      <div className="space-y-6">
        {categories.map(categorie => {
          const contacts = contactsByCategorie[categorie]

          // Group by organisatie to detect subcategories
          const groupedByOrganisatie = contacts.reduce(
            (acc, contact) => {
              const org = contact.organisatie || 'Onbekend'
              if (!acc[org]) {
                acc[org] = []
              }
              acc[org].push(contact)
              return acc
            },
            {} as Record<string, Contactgegeven[]>
          )

          return (
            <Card key={categorie} className="overflow-hidden">
              {/* Category Header - integrated with table */}
              <div className="bg-orange-100 border-b border-orange-200 px-4 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-orange-900">{categorie}</h3>
                  <Badge
                    variant="secondary"
                    className="text-xs bg-orange-200 text-orange-900 hover:bg-orange-300"
                  >
                    {contacts.length} {contacts.length === 1 ? 'contact' : 'contacten'}
                  </Badge>
                </div>
              </div>

              {/* Contacts Table */}
              <div className="overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[300px] !px-4">{t('contacts.organisatie')}</TableHead>
                      <TableHead className="w-[300px] !px-4">{t('contacts.naamFunctie')}</TableHead>
                      <TableHead className="w-[150px] !px-4">{t('contacts.telefoon')}</TableHead>
                      <TableHead className="w-[150px] !px-4">{t('contacts.mobiel')}</TableHead>
                      <TableHead className="w-[200px] !px-4">{t('contacts.email')}</TableHead>
                      <TableHead className="w-[140px] !px-4">
                        {t('contacts.calamiteitennr')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(groupedByOrganisatie).map(([organisatie, orgContacts]) => {
                      const hasMultipleContacts = orgContacts.length > 1
                      const mainContact = orgContacts[0]
                      const rowId = `${categorie}-${organisatie}`
                      const isExpanded = expandedRows.has(rowId)

                      return (
                        <>
                          {/* Main Row - Only shows organization when collapsed */}
                          <TableRow
                            key={rowId}
                            className="hover:bg-muted/50 cursor-pointer py-2"
                            onClick={() => hasMultipleContacts && toggleRow(rowId)}
                          >
                            <TableCell className="w-[300px] font-medium !px-4 py-2">
                              <div className="flex items-center">
                                {hasMultipleContacts ? (
                                  <>
                                    <button
                                      className={`!h-auto !w-auto !p-0 !mr-2 transition-colors inline-flex items-center justify-center${
                                        isExpanded
                                          ? 'text-primary'
                                          : 'text-muted-foreground hover:text-primary'
                                      }`}
                                      onClick={e => {
                                        e.stopPropagation()
                                        toggleRow(rowId)
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </button>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="truncate">{organisatie}</span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{organisatie}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="truncate">{organisatie}</span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{organisatie}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </>
                                )}
                              </div>
                            </TableCell>
                            {!isExpanded && hasMultipleContacts ? (
                              <TableCell className="w-[200px] !px-4 py-2" colSpan={5}>
                                <span className="text-sm text-muted-foreground italic">
                                  Klik om {orgContacts.length} contacten te tonen
                                </span>
                              </TableCell>
                            ) : !hasMultipleContacts ? (
                              <>
                                <TableCell className="w-[200px] !px-4 py-2">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center gap-2">
                                        <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <span className="truncate">
                                          {mainContact.naamFunctie || '-'}
                                        </span>
                                      </div>
                                    </TooltipTrigger>
                                    {mainContact.naamFunctie && (
                                      <TooltipContent>
                                        <p>{mainContact.naamFunctie}</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="w-[180px] !px-4 py-2">
                                  {mainContact.telefoonVast ? (
                                    <a
                                      href={`tel:${mainContact.telefoonVast}`}
                                      className="flex items-center gap-1 text-primary hover:underline"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <Phone className="h-3 w-3" />
                                      <span className="text-sm">{mainContact.telefoonVast}</span>
                                    </a>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell className="w-[180px] !px-4 py-2">
                                  {mainContact.mobiel ? (
                                    <a
                                      href={`tel:${mainContact.mobiel}`}
                                      className="flex items-center gap-1 text-primary hover:underline"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <Phone className="h-3 w-3" />
                                      <span className="text-sm">{mainContact.mobiel}</span>
                                    </a>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell className="w-[220px] !px-4 py-2">
                                  {mainContact.mail ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <a
                                          href={`mailto:${mainContact.mail}`}
                                          className="flex items-center gap-1 text-primary hover:underline truncate"
                                          onClick={e => e.stopPropagation()}
                                        >
                                          <Mail className="h-3 w-3 flex-shrink-0" />
                                          <span className="text-sm truncate">
                                            {mainContact.mail}
                                          </span>
                                        </a>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{mainContact.mail}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell className="w-[160px] !px-4 py-2">
                                  {mainContact.calamiteitennr ? (
                                    <Badge variant="destructive" className="font-mono text-xs">
                                      {mainContact.calamiteitennr}
                                    </Badge>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              </>
                            ) : null}
                          </TableRow>

                          {/* Expanded Contact Rows - Shows ALL contacts when expanded */}
                          {isExpanded &&
                            hasMultipleContacts &&
                            orgContacts.map((contact, idx) => (
                              <TableRow key={`${rowId}-contact-${idx}`} className="bg-muted/30">
                                <TableCell className="w-[300px] !px-4 py-2">
                                  <div className="flex items-center gap-1 pl-6">
                                    <span className="text-muted-foreground text-sm">↳</span>
                                    {contact.subcategorie && (
                                      <Badge variant="outline" className="text-xs">
                                        {contact.subcategorie}
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="w-[200px] !px-4 py-2">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center gap-0">
                                        <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <span className="truncate text-sm">
                                          {contact.naamFunctie || '-'}
                                        </span>
                                      </div>
                                    </TooltipTrigger>
                                    {contact.naamFunctie && (
                                      <TooltipContent>
                                        <p>{contact.naamFunctie}</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="w-[180px] !px-4 py-2">
                                  {contact.telefoonVast ? (
                                    <a
                                      href={`tel:${contact.telefoonVast}`}
                                      className="flex items-center gap-0 text-primary hover:underline"
                                    >
                                      <Phone className="h-3 w-3" />
                                      <span className="text-sm">{contact.telefoonVast}</span>
                                    </a>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell className="w-[180px] !px-4 py-2">
                                  {contact.mobiel ? (
                                    <a
                                      href={`tel:${contact.mobiel}`}
                                      className="flex items-center gap-0 text-primary hover:underline"
                                    >
                                      <Phone className="h-3 w-3" />
                                      <span className="text-sm">{contact.mobiel}</span>
                                    </a>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell className="w-[220px] !px-4 py-2">
                                  {contact.mail ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <a
                                          href={`mailto:${contact.mail}`}
                                          className="flex items-center gap-0 text-primary hover:underline truncate"
                                        >
                                          <Mail className="h-3 w-3 flex-shrink-0" />
                                          <span className="text-sm truncate">{contact.mail}</span>
                                        </a>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{contact.mail}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell className="w-[160px] !px-4 py-2">
                                  {contact.calamiteitennr ? (
                                    <Badge variant="destructive" className="font-mono text-xs">
                                      {contact.calamiteitennr}
                                    </Badge>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )
        })}
      </div>
    )
  }

  return (
    <TooltipProvider>
      <Layout removeBottomPadding={true}>
        {/* Hero Section with Title and Controls */}
        <div className="container mx-auto px-4 md:px-6 mt-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
                {t('contacts.titel')}
              </h1>
              <p className="text-base md:text-xl text-muted-foreground">
                {t('contacts.beschrijving')}
              </p>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-muted-foreground">{t('contacts.ladenContactgegevens')}</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('contacts.foutBijLaden')}:{' '}
                {error instanceof Error ? error.message : String(error)}
              </AlertDescription>
            </Alert>
          )}

          {/* Empty State */}
          {!isLoading && !error && contactgegevens && contactgegevens.length === 0 && (
            <Card className="p-12">
              <div className="text-center">
                <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('contacts.geenContactgevonden')}</h3>
                <p className="text-muted-foreground">{t('contacts.geenContactBeschrijving')}</p>
              </div>
            </Card>
          )}

          {/* Tabbed Data Tables */}
          {!isLoading && !error && contactgegevens && contactgegevens.length > 0 && (
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <TabsList className="bg-muted/50 p-1 h-auto">
                  {bestandTypes.map(bestand => {
                    const shortName = bestand
                      .replace('.xlsx', '')
                      .replace(/\s*\([^)]*\)/g, '')
                      .substring(0, 50)
                    const count = groupedByBestand[bestand]?.length || 0
                    return (
                      <TabsTrigger
                        key={bestand}
                        value={bestand}
                        className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary font-medium transition-all"
                      >
                        {shortName}
                        <Badge
                          variant="secondary"
                          className="ml-1 data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                        >
                          {count}
                        </Badge>
                      </TabsTrigger>
                    )
                  })}
                </TabsList>
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Zoek contacten..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {bestandTypes.map(bestand => (
                <TabsContent key={bestand} value={bestand}>
                  {renderContactsByCategorie(groupedByBestandAndCategorie[bestand] || {})}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </Layout>
    </TooltipProvider>
  )
}
